import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Briefcase, 
  DollarSign, 
  Calculator, 
  CheckCircle, 
  Lightbulb,
  Settings,
  FileText,
  Mail,
  Zap
} from 'lucide-react';

const ServiceTypesGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Service Types & Quote Calculator</h1>
          <p className="text-lg text-gray-600">
            Set up your pricing menu and let SuddyFlow calculate quotes automatically
          </p>
        </div>

        {/* What Are Service Types */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Briefcase className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">What Are Service Types?</h2>
              <p className="text-gray-700">
                Service Types are your service menu - the different services you offer customers. 
                Each service type can have its own pricing method: flat rate, per square foot, tiered pricing, and more.
                When you create a job, SuddyFlow uses your service type settings to calculate quotes automatically.
              </p>
            </div>
          </div>
        </div>

        {/* NEW: Quote Calculator Feature */}
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Calculator className="text-white" size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-xl font-bold text-gray-900">Quote Calculator</h2>
                <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-medium rounded-full">NEW</span>
              </div>
              <p className="text-gray-700 mb-4">
                Configure pricing formulas for your services and SuddyFlow will calculate quotes for you. 
                Enter the square footage, select any add-ons, and get an instant quote - no mental math required!
              </p>
              <div className="bg-white/60 rounded-lg p-4">
                <p className="text-sm font-medium text-purple-900 mb-2">Example: House Wash Quote</p>
                <div className="text-sm text-purple-800 space-y-1">
                  <p>• Home size: 2,500 sq ft × $0.13/sq ft = $325</p>
                  <p>• 2-Story add-on: +$100</p>
                  <p>• <strong>Total Quote: $425</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Methods */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Pricing Methods</h2>
            <p className="text-sm text-gray-600 mt-1">Choose the method that fits each service</p>
          </div>
          <div className="p-6 space-y-6">
            
            {/* Flat Rate */}
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-1">Flat Rate</h3>
              <p className="text-gray-600 text-sm mb-2">One fixed price regardless of size or time.</p>
              <div className="bg-green-50 rounded-lg p-3 text-sm">
                <strong className="text-green-800">Best for:</strong> Standard services with predictable scope
                <div className="mt-1 text-green-700">
                  Example: Gutter cleaning = $175 flat
                </div>
              </div>
            </div>

            {/* Per Square Foot */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-1">Per Square Foot</h3>
              <p className="text-gray-600 text-sm mb-2">Price based on area size. Great for house washing, painting, flooring.</p>
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <strong className="text-blue-800">Best for:</strong> Services priced by home or area size
                <div className="mt-1 text-blue-700">
                  Example: House wash at $0.13/sq ft → 2,000 sq ft home = $260
                </div>
              </div>
            </div>

            {/* Tiered (S/M/L) */}
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-1">Tiered (Small / Medium / Large)</h3>
              <p className="text-gray-600 text-sm mb-2">Preset prices for different sizes. Simple for customers to understand.</p>
              <div className="bg-purple-50 rounded-lg p-3 text-sm">
                <strong className="text-purple-800">Best for:</strong> Driveways, patios, decks, parking lots
                <div className="mt-1 text-purple-700">
                  Example: Driveway - Small: $150 | Medium: $225 | Large: $350
                </div>
              </div>
            </div>

            {/* Per Linear Foot */}
            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-1">Per Linear Foot</h3>
              <p className="text-gray-600 text-sm mb-2">Price by length. Perfect for fencing, gutters, edging.</p>
              <div className="bg-orange-50 rounded-lg p-3 text-sm">
                <strong className="text-orange-800">Best for:</strong> Linear services like fencing or gutter installation
                <div className="mt-1 text-orange-700">
                  Example: Fence staining at $3/linear ft → 200 ft = $600
                </div>
              </div>
            </div>

            {/* Per Item */}
            <div className="border-l-4 border-teal-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-1">Per Item / Count</h3>
              <p className="text-gray-600 text-sm mb-2">Price per unit. Great for window cleaning, tree trimming.</p>
              <div className="bg-teal-50 rounded-lg p-3 text-sm">
                <strong className="text-teal-800">Best for:</strong> Countable items like windows, trees, bushes
                <div className="mt-1 text-teal-700">
                  Example: Window cleaning at $8/window → 25 windows = $200
                </div>
              </div>
            </div>

            {/* Per Hour */}
            <div className="border-l-4 border-gray-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-1">Per Hour</h3>
              <p className="text-gray-600 text-sm mb-2">Hourly rate for unpredictable work.</p>
              <div className="bg-gray-100 rounded-lg p-3 text-sm">
                <strong className="text-gray-800">Best for:</strong> Repairs, consulting, or variable scope work
                <div className="mt-1 text-gray-700">
                  Example: Handyman services = $75/hour
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add-ons / Modifiers */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Add-ons & Modifiers</h2>
            <p className="text-sm text-gray-600 mt-1">Extra charges for special conditions</p>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              Add modifiers to your service types to account for extra work. These appear as checkboxes 
              when creating a quote - just check the ones that apply.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Flat Amount Add-ons</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 2-Story: +$100</li>
                  <li>• 3-Story: +$200</li>
                  <li>• Detached Garage: +$75</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Percentage Add-ons</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Heavy Staining: +25%</li>
                  <li>• Rush Service: +50%</li>
                  <li>• Weekend Rate: +15%</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* How to Set Up */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Settings className="text-gray-600" size={20} />
              <h2 className="text-xl font-bold text-gray-900">How to Set Up Service Types</h2>
            </div>
          </div>
          <div className="p-6">
            <ol className="space-y-4">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">1</span>
                <div>
                  <p className="font-medium text-gray-900">Go to Settings → Service Types</p>
                  <p className="text-sm text-gray-600">Click the gear icon in the sidebar, then select the Service Types tab</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">2</span>
                <div>
                  <p className="font-medium text-gray-900">Add a New Service Type</p>
                  <p className="text-sm text-gray-600">Click "Add Service Type" and enter the name and category</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">3</span>
                <div>
                  <p className="font-medium text-gray-900">Enable Quote Calculator</p>
                  <p className="text-sm text-gray-600">Toggle on "Enable Quote Calculator" to configure pricing formulas</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">4</span>
                <div>
                  <p className="font-medium text-gray-900">Choose Pricing Method</p>
                  <p className="text-sm text-gray-600">Select flat rate, per sq ft, tiered, etc. and enter your rates</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">5</span>
                <div>
                  <p className="font-medium text-gray-900">Add Modifiers (Optional)</p>
                  <p className="text-sm text-gray-600">Add any upcharges like "2-Story" or "Heavy Staining"</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">6</span>
                <div>
                  <p className="font-medium text-gray-900">Save and Use!</p>
                  <p className="text-sm text-gray-600">Your calculator will appear when creating jobs with this service type</p>
                </div>
              </li>
            </ol>
          </div>
        </div>

        {/* Using the Calculator */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Calculator className="text-gray-600" size={20} />
              <h2 className="text-xl font-bold text-gray-900">Using the Quote Calculator</h2>
            </div>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              When you create a job and select a service type with calculator enabled, the Quote Calculator 
              appears automatically. Here's how it works:
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-gray-900">Enter the measurement</p>
                  <p className="text-sm text-gray-600">Square footage, linear feet, item count, or select a tier size</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-gray-900">Check any applicable add-ons</p>
                  <p className="text-sm text-gray-600">2-Story, heavy staining, rush service, etc.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-gray-900">Quote auto-calculates</p>
                  <p className="text-sm text-gray-600">The total fills in automatically - you can still override if needed</p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-blue-800">
                  <strong>Pro Tip:</strong> You can always manually adjust the calculated quote. 
                  The calculator gives you a starting point, but you have final control over the price.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sending Quotes */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Mail className="text-gray-600" size={20} />
              <h2 className="text-xl font-bold text-gray-900">Sending Quotes to Customers</h2>
            </div>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              Once you've created a job with status "Quoted", you can email the quote to your customer 
              with one click.
            </p>
            
            <ol className="space-y-3 mb-4">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-medium">1</span>
                <p className="text-gray-700">Create a job with the calculated quote amount</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-medium">2</span>
                <p className="text-gray-700">Set status to "Quoted"</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-medium">3</span>
                <p className="text-gray-700">Click the purple "Send Quote" button on the job card</p>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-medium">4</span>
                <p className="text-gray-700">Customer receives a professional quote email</p>
              </li>
            </ol>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> The "Send Quote" button only appears when the customer has an email 
                address and the quote hasn't been sent yet. After sending, you'll see a "Quote Sent" badge.
              </p>
            </div>
          </div>
        </div>

        {/* Common Questions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Common Questions</h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I change the calculated quote?
              </h3>
              <p className="text-gray-700 text-sm">
                Absolutely! The calculator gives you a starting point, but you can always adjust the final 
                quote amount. Just edit the Quote Amount field after the calculator fills it in.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What if I don't want to use the calculator?
              </h3>
              <p className="text-gray-700 text-sm">
                You can leave the "Enable Quote Calculator" toggle off when setting up a service type. 
                You'll just enter quote amounts manually like before.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Will customers see my pricing formula?
              </h3>
              <p className="text-gray-700 text-sm">
                No! When you send a quote email, customers only see the service name and total amount. 
                Your per-square-foot rates and calculation details stay private.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I have different prices for different customers?
              </h3>
              <p className="text-gray-700 text-sm">
                Yes! The calculator gives you a baseline, but you can adjust individual quotes however you like. 
                Give discounts to repeat customers, add charges for difficult access, etc.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What's the minimum charge for?
              </h3>
              <p className="text-gray-700 text-sm">
                Set a minimum to ensure small jobs are still profitable. For example, if your rate is $0.13/sq ft 
                but a tiny porch is only 100 sq ft ($13), your $150 minimum kicks in instead.
              </p>
            </div>
          </div>
        </div>

        {/* Pro Tips */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Zap className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Pro Tips</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
                  <span><strong>Set minimums wisely</strong> - Account for travel time and setup in your minimum charge</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
                  <span><strong>Use tiered pricing for driveways</strong> - Customers understand "small/medium/large" better than square footage</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
                  <span><strong>Add story upcharges</strong> - Multi-story homes take more time and equipment</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
                  <span><strong>Review your rates quarterly</strong> - Adjust for fuel costs, equipment wear, and market rates</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
                  <span><strong>Where to find square footage</strong> - Check Zillow, property tax records, or ask the customer</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Need Help */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
          <h3 className="font-medium text-primary-900 mb-2">Need Help?</h3>
          <p className="text-sm text-primary-700">
            Have questions about setting up your service types or using the quote calculator? 
            We're here to help! Contact our support team or check out our other help guides.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServiceTypesGuide;